/**
 * In-memory sparse chunked tile storage for one layer — the client twin of the
 * engine's TilemapComponent chunk map (engine 45.2) and of the server's
 * graph_tilemap_chunks rows. Chunks exist only where painted; each is a dense
 * Uint32Array of CHUNK_AREA tile ids plus dirty flags:
 *
 *   - renderDirty: the chunk's cached bitmap must be rebuilt (ChunkRenderer)
 *   - saveDirty:   the chunk changed since the last autosave flush
 *
 * A chunk whose used count reaches 0 is dropped from the map (sparse
 * invariant) and marked saveDirty so the flush deletes its row.
 */

import {
    CHUNK_AREA,
    CHUNK_SIZE,
    CHUNK_SHIFT,
    EMPTY_TILE,
    cellToChunk,
    type TileID,
} from './constants';
import { decodeChunk, encodeChunk, isChunkEmpty } from './rle';

export interface Chunk {
    cx: number;
    cy: number;
    tiles: Uint32Array; // length CHUNK_AREA, row-major, y-up rows
    usedCount: number;
    renderDirty: boolean;
    saveDirty: boolean;
}

/** Map key for a chunk coordinate. */
export function chunkKey(cx: number, cy: number): string {
    return `${cx},${cy}`;
}

/** Local row-major index of a cell within its chunk. */
function localIndex(cellX: number, cellY: number): number {
    const lx = cellX - (cellToChunk(cellX) << CHUNK_SHIFT);
    const ly = cellY - (cellToChunk(cellY) << CHUNK_SHIFT);
    return ly * CHUNK_SIZE + lx;
}

/**
 * The tile data of a single layer: a sparse map of chunks, addressed by cell.
 * Pure model — no canvas, no React, no network. The surface renders from it,
 * tools mutate it, autosave serializes its dirty chunks.
 */
export class LayerTiles {
    private chunks = new Map<string, Chunk>();

    /** Chunk coords whose row must be upserted/deleted on next flush. */
    private dirtyChunkKeys = new Set<string>();

    /** Read a cell; empty (0) where no chunk exists. */
    getTile(cellX: number, cellY: number): TileID {
        const chunk = this.chunks.get(chunkKey(cellToChunk(cellX), cellToChunk(cellY)));
        return chunk ? chunk.tiles[localIndex(cellX, cellY)] : EMPTY_TILE;
    }

    /**
     * Write a cell. Returns true if the value actually changed (tools use this
     * to record undo edits and dirty only what moved). Maintains usedCount,
     * dirty flags, and the sparse invariant.
     */
    setTile(cellX: number, cellY: number, id: TileID): boolean {
        const cx = cellToChunk(cellX);
        const cy = cellToChunk(cellY);
        const key = chunkKey(cx, cy);
        let chunk = this.chunks.get(key);

        if (!chunk) {
            if (id === EMPTY_TILE) return false; // erasing empty space: no-op
            chunk = {
                cx, cy,
                tiles: new Uint32Array(CHUNK_AREA),
                usedCount: 0,
                renderDirty: true,
                saveDirty: true,
            };
            this.chunks.set(key, chunk);
        }

        const idx = localIndex(cellX, cellY);
        const prev = chunk.tiles[idx];
        if (prev === id) return false;

        chunk.tiles[idx] = id;
        if (prev === EMPTY_TILE && id !== EMPTY_TILE) chunk.usedCount++;
        else if (prev !== EMPTY_TILE && id === EMPTY_TILE) chunk.usedCount--;

        chunk.renderDirty = true;
        chunk.saveDirty = true;
        this.dirtyChunkKeys.add(key);

        // Emptied chunk → drop it (sparse invariant); the flush deletes the row.
        if (chunk.usedCount === 0) {
            this.chunks.delete(key);
        }
        return true;
    }

    /** Load a chunk from a decoded base64 payload (from the API). */
    loadChunkBase64(cx: number, cy: number, data: string): void {
        const tiles = decodeChunk(data);
        if (isChunkEmpty(tiles)) return; // never hold an all-empty chunk
        let used = 0;
        for (let i = 0; i < CHUNK_AREA; i++) if (tiles[i] !== EMPTY_TILE) used++;
        this.chunks.set(chunkKey(cx, cy), {
            cx, cy, tiles, usedCount: used, renderDirty: true, saveDirty: false,
        });
    }

    /** True if this layer already has (or once loaded) the given chunk. */
    hasChunk(cx: number, cy: number): boolean {
        return this.chunks.has(chunkKey(cx, cy));
    }

    getChunk(cx: number, cy: number): Chunk | undefined {
        return this.chunks.get(chunkKey(cx, cy));
    }

    /** Every present chunk. */
    allChunks(): IterableIterator<Chunk> {
        return this.chunks.values();
    }

    /** Total painted tiles across the layer. */
    totalUsed(): number {
        let n = 0;
        for (const c of this.chunks.values()) n += c.usedCount;
        return n;
    }

    /** Whether anything needs saving. */
    hasSaveDirty(): boolean {
        return this.dirtyChunkKeys.size > 0;
    }

    /**
     * Re-queue a chunk coordinate for the next flush. Used to retry after a
     * failed/conflicting autosave (the drain that built the failed payload
     * already cleared these keys). A coord with no live chunk re-emits as a
     * delete, which is correct.
     */
    requeueForSave(cx: number, cy: number): void {
        this.dirtyChunkKeys.add(chunkKey(cx, cy));
    }

    /**
     * Drain the save-dirty chunks into a flush payload. A key that no longer
     * maps to a chunk (emptied) becomes `data: null` — a delete. Clears the
     * dirty set and per-chunk saveDirty flags; call only when a flush is
     * committed.
     */
    drainDirtyForSave(): Array<{ cx: number; cy: number; data: string | null }> {
        const out: Array<{ cx: number; cy: number; data: string | null }> = [];
        for (const key of this.dirtyChunkKeys) {
            const chunk = this.chunks.get(key);
            const [cx, cy] = key.split(',').map(Number);
            if (!chunk) {
                out.push({ cx, cy, data: null }); // deleted chunk
            } else {
                chunk.saveDirty = false;
                out.push({ cx, cy, data: encodeChunk(chunk.tiles) });
            }
        }
        this.dirtyChunkKeys.clear();
        return out;
    }
}
